/** Team management page — invite/revoke control-plane staff (operator/finance). */
import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import { format } from "date-fns";
import { staffApi } from "../../api/client";

/** Minimal staff-user shape returned by GET /api/v1/staff. */
interface StaffUser {
  id: string;
  username: string;
  email: string;
  role: string;
  reseller_id: string | null;
  is_active: boolean;
  created_at: string;
  last_login: string | null;
}

const ROLE_CHOICES = [
  { value: "operator", label: "Operator — monitoring, alerts, CDRs" },
  { value: "finance", label: "Finance — billing, invoices, payments" },
];

export default function Team() {
  const [staff, setStaff] = useState<StaffUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Invite dialog state
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteUsername, setInviteUsername] = useState("");
  const [inviteRole, setInviteRole] = useState<"operator" | "finance">("operator");
  const [inviting, setInviting] = useState(false);
  const [inviteUrl, setInviteUrl] = useState("");
  const [toast, setToast] = useState("");

  // Revoke confirmation state
  const [revokeTarget, setRevokeTarget] = useState<StaffUser | null>(null);

  const loadStaff = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await staffApi.list();
      setStaff(res.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to load staff users");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStaff();
  }, [loadStaff]);

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    setInviting(true);
    setError("");
    try {
      const res = await staffApi.invite({
        email: inviteEmail.trim(),
        username: inviteUsername.trim() || undefined,
        role: inviteRole,
      });
      // invite_url is only returned when SMTP is unconfigured — relay it
      // out-of-band in that case.
      setInviteUrl(res.data?.invite_url || "");
      setToast(`Invitation created for ${res.data?.email || inviteEmail}`);
      setInviteOpen(false);
      setInviteEmail("");
      setInviteUsername("");
      setInviteRole("operator");
      loadStaff();
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to create invitation");
    } finally {
      setInviting(false);
    }
  };

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    setError("");
    try {
      await staffApi.remove(revokeTarget.id);
      setToast(`Revoked access for ${revokeTarget.username}`);
      setRevokeTarget(null);
      loadStaff();
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to revoke staff user");
      setRevokeTarget(null);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h5">Team</Typography>
        <Button
          variant="contained"
          startIcon={<PersonAddAlt1Icon />}
          onClick={() => {
            setInviteUrl("");
            setInviteOpen(true);
          }}
        >
          Invite staff member
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Staff roles gate admin-surface features: <strong>operators</strong> see monitoring,
        alerts, CDRs and test calls; <strong>finance</strong> sees billing, invoices and
        payments. Access is provisioned via single-use invitation links — never shared
        passwords. Revocation is instant.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {inviteUrl && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          onClose={() => setInviteUrl("")}
          action={
            <IconButton
              color="inherit"
              size="small"
              onClick={() => navigator.clipboard.writeText(inviteUrl)}
              aria-label="Copy invitation link"
            >
              <ContentCopyIcon fontSize="inherit" />
            </IconButton>
          }
        >
          SMTP is not configured — relay this invitation link to the invitee:{" "}
          <Box component="code" sx={{ wordBreak: "break-all" }}>
            {inviteUrl}
          </Box>
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Username</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Last login</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6}>Loading…</TableCell>
              </TableRow>
            ) : staff.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6}>No staff users yet — invite one to get started.</TableCell>
              </TableRow>
            ) : (
              staff.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>{user.username}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={user.role === "operator" ? "primary" : "secondary"}
                      label={user.role}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={user.is_active ? "success" : "default"}
                      label={user.is_active ? "active" : "disabled"}
                      variant={user.is_active ? "filled" : "outlined"}
                    />
                  </TableCell>
                  <TableCell>
                    {user.last_login ? format(new Date(user.last_login), "yyyy-MM-dd HH:mm") : "never"}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Revoke access">
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          disabled={!user.is_active}
                          onClick={() => setRevokeTarget(user)}
                          aria-label={`Revoke ${user.username}`}
                        >
                          <PersonRemoveIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Invite dialog */}
      <Dialog open={inviteOpen} onClose={() => setInviteOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Invite a staff member</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            The invitee receives a single-use link (valid 72 hours) to set their own
            password. When email delivery is not configured, the link is shown here for
            out-of-band relay.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Email"
            type="email"
            fullWidth
            required
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Username (optional — derived from email if blank)"
            fullWidth
            value={inviteUsername}
            onChange={(e) => setInviteUsername(e.target.value)}
          />
          <Select
            fullWidth
            value={inviteRole}
            onChange={(e) => setInviteRole(e.target.value as "operator" | "finance")}
            sx={{ mt: 2 }}
          >
            {ROLE_CHOICES.map((choice) => (
              <MenuItem key={choice.value} value={choice.value}>
                {choice.label}
              </MenuItem>
            ))}
          </Select>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteOpen(false)}>Cancel</Button>
          <Button onClick={handleInvite} variant="contained" disabled={inviting || !inviteEmail.trim()}>
            {inviting ? "Inviting…" : "Send invitation"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revoke confirmation */}
      <Dialog open={revokeTarget !== null} onClose={() => setRevokeTarget(null)}>
        <DialogTitle>Revoke access?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {revokeTarget && (
              <>
                Disable <strong>{revokeTarget.username}</strong> ({revokeTarget.email})?
                Their active sessions are invalidated immediately.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevokeTarget(null)}>Cancel</Button>
          <Button onClick={handleRevoke} color="error" variant="contained">
            Revoke
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast !== ""}
        autoHideDuration={5000}
        onClose={() => setToast("")}
        message={toast}
      />
    </Box>
  );
}
